/**
 * Asset-class classification — ONE definition, shared by server and client.
 *
 * Why this file exists: nine places independently decided whether a symbol was
 * crypto, using four different tests. The server's contagion clustering — the
 * code that decides whether a correlated cluster is "cross-asset", which is the
 * single most load-bearing claim Fahali makes — used the narrowest and most
 * wrong of them:
 *
 *     symbol.endsWith("USDT") ? "crypto" : "equity"
 *
 * Measured against 24h of live alerts (3,885 symbols): 447 end in USDT, and
 * **166 are crypto pairs quoted in something else** — PEPEUSDC, SHIBUSDC,
 * BONKUSDC, DASHUSDC and 162 more. Every one of those was labelled "equity", so
 * a cluster of BTCUSDT + SHIBUSDC was published as CROSS-ASSET CONTAGION when
 * both legs are crypto. The flagship claim was being manufactured by a string
 * test.
 *
 * A client screen and an acceptance test each used broader tests (USDT|USDC,
 * and USDT|*BTC|BTC|ETH), so server-computed and client-rendered asset classes
 * could disagree about the same symbol.
 *
 * The quote list below is derived from the symbols actually present in the
 * alerts table, not guessed.
 */

/*
  Quote assets, derived from 48h of live alerts rather than assumed.
  Measured occurrence (symbols ending in each, base non-empty):

    USDT  447    USDC  169
    FDUSD  0     EURI   0     BUSD  0    TUSD  0    USD1  0    XUSD  0
    DAI    1  -> IDAI                       (an equity)
    BNB    3  -> ABNB, HBNB, VBNB           (ABNB is Airbnb)
    BTC    3  -> ABTC, OBTC, YBTC           (spot-bitcoin ETFs)
    ETH    5  -> FETH, QETH, TETH, WETH, YETH  (ether ETFs)

  So the only crypto quotes present are USDT and USDC. Including BTC/ETH/BNB/DAI
  bought twelve false positives and zero true ones — it would have relabelled
  Airbnb as crypto.

  The ETF rows matter in their own right: a spot-bitcoin ETF trades on an equity
  venue, on equity hours, so for contagion purposes it IS an equity. That makes
  BTCUSDT vs YBTC a genuine cross-asset pair, which is exactly the relationship
  worth detecting — and it only reads correctly if the ETF stays classified as
  equity.

  If a crypto pair quoted in something else is ever ingested, add the quote here
  after checking it does not collide with a US ticker.
*/
export const CRYPTO_QUOTE_ASSETS: readonly string[] = ["USDT", "USDC"];

/** Bare crypto tickers that appear without a quote asset — index rows and any
 *  caller that names a coin rather than a pair (e.g. "BTC", "ETH"). */
const BARE_CRYPTO = new Set(["BTC", "ETH", "SOL", "XRP", "BNB", "DOGE", "ADA"]);

/**
 * Canonical trading pair for a bare crypto ticker.
 *
 * Measured 2026-07-25: a visitor typing **BTC** got `reliability: UNVALIDATED`,
 * `state: WITHHOLD`, record **0/0** — while the identical asset under **BTCUSDT**
 * returned `WATCH` on a record of **50/1,143 resolved outcomes**. Same asset, two
 * names, opposite answers.
 *
 * That is not a rounding error in the funnel: the landing widget's placeholder is
 * literally "BTC" and the live ticker emits "BTC", so the site steers strangers
 * into the lookup that finds nothing, and the first thing Fahali tells them is
 * that it has no evidence.
 *
 * Only the unambiguous majors are mapped. Deliberately NOT a general rule: some
 * bare tickers collide with US equities, and silently turning an equity query
 * into a crypto answer would be a worse bug than the one being fixed. If a
 * collision is ever discovered here, remove that ticker rather than adding
 * heuristics.
 */
const BARE_TO_PAIR: Readonly<Record<string, string>> = {
  BTC: "BTCUSDT",
  ETH: "ETHUSDT",
  SOL: "SOLUSDT",
  XRP: "XRPUSDT",
  BNB: "BNBUSDT",
  DOGE: "DOGEUSDT",
  ADA: "ADAUSDT",
};

/**
 * Resolve what the user typed to what the data is keyed on.
 * Returns the input unchanged for anything not an unambiguous bare major, so
 * equities and already-paired symbols pass straight through.
 */
export function canonicalPairFor(symbol: string | null | undefined): string {
  const s = String(symbol ?? "").trim().toUpperCase();
  return BARE_TO_PAIR[s] ?? s;
}

export type AssetClass = "crypto" | "equity";

/**
 * Classify a trading symbol.
 *
 * Deliberately conservative: a symbol counts as crypto only when it is a known
 * bare ticker, or when it ends in a quote asset that actually occurs in our data
 * AND leaves a non-empty base behind. Short ambiguous suffixes are excluded on
 * purpose — see the measurement above.
 *
 * Known limitation, stated rather than guessed at: Binance lists tokenized
 * equities that are quoted in USDT and would classify here as crypto. They are
 * already excluded upstream from volatility-V2 reads (see the 2026-07-17
 * amendment). If a canonical list of those tickers exists, add it here as an
 * override — do not infer it from the ticker shape.
 */
export function classifyAssetClass(symbol: string | null | undefined): AssetClass {
  const s = String(symbol ?? "").trim().toUpperCase();
  if (!s) return "equity";
  if (BARE_CRYPTO.has(s)) return "crypto";
  for (const quote of CRYPTO_QUOTE_ASSETS) {
    if (s.length > quote.length && s.endsWith(quote)) return "crypto";
  }
  return "equity";
}

/** True when a set of symbols spans more than one asset class. This is the test
 *  that earns the phrase "cross-asset" — nothing else should assert it. */
export function isCrossAsset(symbols: readonly (string | null | undefined)[]): boolean {
  const classes = new Set(symbols.map(classifyAssetClass));
  return classes.size > 1;
}

/** Category label for a correlated cluster. */
export function clusterAssetCategory(
  symbols: readonly (string | null | undefined)[],
): "cross-asset" | "crypto" | "equity" {
  const classes = new Set(symbols.map(classifyAssetClass));
  if (classes.size > 1) return "cross-asset";
  return classes.has("crypto") ? "crypto" : "equity";
}
